/**
 * Refonte Achats · Stock WP-13 (A-11, ux/04 §3.1) — does a row match a query, and how well.
 *
 * Matching: EVERY token must be a substring of the row's key (`search_text`), in any order; a token
 * of RECHERCHE_PROCHE_MIN_CARACTERES characters or more with no substring match may still match a
 * word by trigram similarity (typo tier). Ranking (lower = better):
 *   EXACT 0        the whole query (spaces removed) is one of the row's barcodes or its N° ENR
 *   PREFIXE_NOM 1  every token prefixes a word and the first token prefixes the nom commercial
 *   PREFIXE 2      every token prefixes a word somewhere
 *   SOUS_CHAINE 3  every token is a substring
 *   PROCHE 4       at least one token matched by similarity only (« Orthographe proche »)
 * Ties: the caller's surface rule (caisse: sellable first; achat: the achat's type first), then the
 * name A→Z, then the id — deterministic, identical online and offline.
 */
import { normaliserRecherche, compacterRecherche } from "./normalise.js";
import { preparerRequete } from "./tokenise.js";
import { meilleureSimilarite } from "./trigram.js";
import { champsNormalises, codesDuProduit, cleRechercheProduit, cleRechercheNomenclature } from "./cle.js";
import {
  NIVEAU_PERTINENCE,
  RECHERCHE_SEUIL_SIMILARITE,
  RECHERCHE_PROCHE_MIN_CARACTERES,
} from "./constantes.js";

const LETTRE = /\p{L}/u;

/**
 * A token may match by similarity only with enough characters and at least one letter: a number
 * (a dosage, a barcode, a N° ENR fragment) is never « close » to another number (wrong-product risk,
 * AUD-SRCH-09).
 */
const admisProche = (t, min) => t.length >= min && LETTRE.test(t);

const NIVEAU_PAR_RANG = Object.freeze(Object.fromEntries(Object.entries(NIVEAU_PERTINENCE).map(([k, v]) => [v, k])));

/**
 * The searchable view of a row: its key (the stored `search_text` when present, else rebuilt), its
 * words, its normalised name and its exact identifiers (codes, N° ENR — spaces removed).
 */
export function documentRecherche(row, { nomenclature = false } = {}) {
  const texte = row?.search_text ?? (nomenclature ? cleRechercheNomenclature(row) : cleRechercheProduit(row)) ?? "";
  const exacts = new Set();
  if (nomenclature) {
    for (const v of [row?.num_enregistrement, row?.code]) {
      const c = compacterRecherche(v);
      if (c) exacts.add(c);
    }
  } else {
    for (const v of [...codesDuProduit(row), row?.num_enr_cnas]) {
      const c = compacterRecherche(v);
      if (c) exacts.add(c);
    }
  }
  return {
    texte,
    mots: texte ? texte.split(" ") : [],
    nom: normaliserRecherche(nomenclature ? row?.nom_marque : row?.nom_commercial),
    exacts,
  };
}

/**
 * @param {ReturnType<typeof documentRecherche>} doc
 * @param {ReturnType<typeof preparerRequete>} requete
 * @param {{ variantesCode?: string[], proche?: boolean, seuilSimilarite?: number, procheMinCaracteres?: number }} [opts]
 *   variantesCode — other spellings of the query that identify the same item exactly (the GS1
 *   equivalent forms of a scanned code, from the barcode module); proche — false disables the typo tier.
 * @returns {null | { niveau: string, rang: number, similarite: number|null }}
 */
export function classer(doc, requete, {
  variantesCode = [],
  proche = true,
  seuilSimilarite = RECHERCHE_SEUIL_SIMILARITE,
  procheMinCaracteres = RECHERCHE_PROCHE_MIN_CARACTERES,
} = {}) {
  if (!requete?.valide || !doc) return null;
  if (doc.exacts.has(requete.compact) || variantesCode.some((v) => doc.exacts.has(compacterRecherche(v)))) {
    return { niveau: "EXACT", rang: NIVEAU_PERTINENCE.EXACT, similarite: null };
  }
  let tousPrefixes = true;
  let similariteMin = null;
  for (const t of requete.tokens) {
    if (doc.texte.includes(t)) {
      if (tousPrefixes && !doc.mots.some((m) => m.startsWith(t))) tousPrefixes = false;
      continue;
    }
    tousPrefixes = false;
    if (!proche || !admisProche(t, procheMinCaracteres)) return null;
    const s = meilleureSimilarite(t, doc.mots);
    if (s < seuilSimilarite) return null;
    similariteMin = similariteMin == null ? s : Math.min(similariteMin, s);
  }
  let rang;
  if (similariteMin != null) rang = NIVEAU_PERTINENCE.PROCHE;
  else if (tousPrefixes) rang = doc.nom.startsWith(requete.tokens[0]) ? NIVEAU_PERTINENCE.PREFIXE_NOM : NIVEAU_PERTINENCE.PREFIXE;
  else rang = NIVEAU_PERTINENCE.SOUS_CHAINE;
  return { niveau: NIVEAU_PAR_RANG[rang], rang, similarite: similariteMin == null ? null : Math.round(similariteMin * 1000) / 1000 };
}

/**
 * The names of the fields a query matched on (for highlighting a field that is not displayed — a
 * barcode, the N° ENR — ux/04 §3.2). Order = field order; `codes_barre` once.
 */
export function champsCorrespondants(row, requete, { nomenclature = false, seuilSimilarite = RECHERCHE_SEUIL_SIMILARITE, procheMinCaracteres = RECHERCHE_PROCHE_MIN_CARACTERES } = {}) {
  if (!requete?.valide) return [];
  const out = [];
  for (const { cle, valeur } of champsNormalises(row, { nomenclature })) {
    if (out.includes(cle)) continue;
    const mots = valeur.split(" ");
    const touche =
      compacterRecherche(valeur) === requete.compact ||
      requete.tokens.some((t) => valeur.includes(t) || (admisProche(t, procheMinCaracteres) && meilleureSimilarite(t, mots) >= seuilSimilarite));
    if (touche) out.push(cle);
  }
  return out;
}

/** Comparator on two `classer` results (better first). */
export function comparerPertinence(a, b) {
  return a.rang - b.rang || (b.similarite ?? 0) - (a.similarite ?? 0);
}

const comparerTexte = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

/**
 * Ranks rows already in memory (the offline store, an in-results filter): keeps the matching rows,
 * best first, each with `pertinence: { niveau, rang, similarite, champs }`.
 *
 * @param {object[]} rows
 * @param {string|ReturnType<typeof preparerRequete>} requete
 * @param {{ nomenclature?: boolean, departager?: (a: object, b: object) => number, limite?: number,
 *           variantesCode?: string[], proche?: boolean }} [opts]
 */
export function rechercherDans(rows, requete, { nomenclature = false, departager, limite, variantesCode, proche } = {}) {
  const req = typeof requete === "string" ? preparerRequete(requete) : requete;
  if (!req?.valide) return [];
  const touches = [];
  for (const row of rows ?? []) {
    const doc = documentRecherche(row, { nomenclature });
    const p = classer(doc, req, { variantesCode, proche });
    if (p) touches.push({ row, p, nom: doc.nom });
  }
  touches.sort((x, y) =>
    comparerPertinence(x.p, y.p) ||
    (departager ? departager(x.row, y.row) : 0) ||
    comparerTexte(x.nom, y.nom) ||
    comparerTexte(String(x.row?.id ?? ""), String(y.row?.id ?? "")));
  const garde = limite == null ? touches : touches.slice(0, limite);
  return garde.map(({ row, p }) => ({ ...row, pertinence: { ...p, champs: champsCorrespondants(row, req, { nomenclature }) } }));
}
